import React, { useEffect, useState } from "react";
import ApiService from "../api/ApiService";
import AppContext from "../context/AppContext";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import notification from "../helpers/notification";
import { ToastContainer } from "react-toastify";

const getStatusColor = (status) => {
  switch (status) {
    case "Scheduled":
      return "bg-primary bg-opacity-25 text-primary";
    case "Ongoing":
      return "bg-warning bg-opacity-25 text-warning";
    case "Complete":
      return "bg-success bg-opacity-25 text-success";
    default:
      return "";
  }
};

const ScheduleCalendar = () => {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = React.useState(today.getMonth());
  const [currentYear, setCurrentYear] = React.useState(today.getFullYear());
  const { user } = React.useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  console.log("selectedCell:", selectedCell);

  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(57);
  const [period, setPeriod] = useState("PM");
  const [location, setLocation] = useState("");

  const daysOfWeek =
    i18n.language === "hi"
      ? ["सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि", "रवि"]
      : ["MON", "TUE", "WED", "THUR", "FRI", "SAT", "SUN"];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOffset = (month, year) => {
    const day = new Date(year, month, 1).getDay(); // Sunday = 0
    return (day + 6) % 7; // Convert to Monday = 0
  };

  const totalDays = getDaysInMonth(currentMonth, currentYear);
  const firstDayOffset = getFirstDayOffset(currentMonth, currentYear);

  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  const prevMonthDays = getDaysInMonth(prevMonth, prevMonthYear);

  // Previous month's trailing days
  const leadingDays = Array.from({ length: firstDayOffset }, (_, i) => ({
    day: prevMonthDays - firstDayOffset + i + 1,
    currentMonth: false,
    month: prevMonth,
    year: prevMonthYear,
  }));

  // Current month days
  const currentDays = Array.from({ length: totalDays }, (_, i) => ({
    day: i + 1,
    currentMonth: true,
    month: currentMonth,
    year: currentYear,
  }));

  // Next month's leading days to fill the last week
  const totalCells = leadingDays.length + currentDays.length;
  const trailingDaysCount = (7 - (totalCells % 7)) % 7;
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

  const trailingDays = Array.from({ length: trailingDaysCount }, (_, i) => ({
    day: i + 1,
    currentMonth: false,
    month: nextMonth,
    year: nextMonthYear,
  }));

  // Final calendar cells
  const calendarCells = [...leadingDays, ...currentDays, ...trailingDays];

  const rows = [];
  for (let i = 0; i < calendarCells.length; i += 7) {
    rows.push(calendarCells.slice(i, i + 7));
  }

  const [calendarData, setCalendarData] = React.useState({});

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const temple_name = user?.temple_name;
        const response = await ApiService.getUpcomingCalendarSchedules({
          params: { temple_name: temple_name },
        });

        const data = response?.data || [];
        const now = new Date();
        const newCalendarData = {};

        data.forEach((item) => {
          const scheduleDate = new Date(`${item.date}T${item.time}`);

          // Filter by current calendar month & year
          if (
            scheduleDate.getMonth() !== currentMonth ||
            scheduleDate.getFullYear() !== currentYear
          )
            return;

          const day = scheduleDate.getDate();

          const endTime = new Date(scheduleDate.getTime() + 2 * 60 * 60 * 1000); // schedule time + 2 hours

          let statusKey = "Scheduled";

          if (item.is_completed) {
            statusKey = "Complete";
          } else if (now > endTime) {
            statusKey = "Complete";
          } else if (scheduleDate <= now) {
            statusKey = "Ongoing";
          }

          const entry = {
            time: item.time,
            temple: item.temple_name || "",
            status: statusKey, // Keep untranslated status
            location: item.location || "",
          };

          if (!newCalendarData[day]) newCalendarData[day] = [];
          newCalendarData[day].push(entry);
        });

        setCalendarData(newCalendarData);
      } catch (err) {
        console.error("Error fetching schedules:", err);
      }
    };

    fetchSchedules();
    const interval = setInterval(fetchSchedules, 30000);
    return () => clearInterval(interval);
  }, [currentMonth, currentYear]);

  const convertTo12HourFormat = (timeStr) => {
    const [hourStr, minute] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minute} ${ampm}`;
  };

  const handleCellClick = (entry, cell) => {
    setSelectedCell({ ...cell, schedule: entry });

    const formatted = convertTo12HourFormat(entry.time);
    const [h, minPeriod] = formatted.split(":");
    const [m, per] = minPeriod.split(" ");

    setHour(parseInt(h));
    setMinute(parseInt(m));
    setPeriod(per);
    setLocation(entry.location || "");

    setIsModalOpen(true);
  };

  const incrementHour = () => {
    setHour((prev) => (prev === 12 ? 1 : prev + 1));
  };

  const decrementHour = () => {
    setHour((prev) => (prev === 1 ? 12 : prev - 1));
  };

  const incrementMinute = () => {
    setMinute((prev) => (prev === 59 ? 0 : prev + 1));
  };

  const decrementMinute = () => {
    setMinute((prev) => (prev === 0 ? 59 : prev - 1));
  };

  const formatDate = (cell) => {
    const yyyy = cell.year;
    const mm = String(cell.month + 1).padStart(2, "0");
    const dd = String(cell.day).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleSave = async () => {
    try {
      const temple_name = user?.temple_name;

      const month = String(selectedCell.month + 1).padStart(2, "0");
      const day = String(selectedCell.day).padStart(2, "0");
      const date = `${selectedCell.year}-${month}-${day}`;

      const payload = {};

      // Allow time where minute = 0
      if (hour != null && minute != null) {
        payload.time = `${hour}:${minute
          .toString()
          .padStart(2, "0")} ${period}`;
      }

      // Add location only if provided
      if (location && location.trim() !== "") {
        payload.location = location;
      }

      if (Object.keys(payload).length === 0) {
        notification("error", "Nothing to update.");
        return;
      }

      await ApiService.updateSankirtanSchedule({
        temple_name,
        date,
        payload,
      });

      notification("success", "Schedule updated successfully.");
      setIsModalOpen(false);

      // Refresh calendar
      window.location.reload();
    } catch (err) {
      console.error(err);
      notification("error", "Failed to update schedule.");
    }
  };

  return (
    <div>

    <ToastContainer />
      <div className="calendar-container w-90% p-5">
        <div
          className="max-w-5xl bg-white mx-auto"
          style={{ paddingBottom: "2%", paddingTop: "1%" }}
        >
          {/* Month navigation */}
          <div
            className="month-nav d-flex justify-content-between align-items-center"
            style={{
              width: "95%",
              justifySelf: "center",
              padding: "5px",
              marginBottom: "1%",
            }}
          >
            <button
              className="month-nav-button btn btn-sm btn-light"
              onClick={handlePrevMonth}
              style={{ width: "4%", color: "#9F1D21" }}
              type="button"
            >
              <span className="fs-4">&lt;</span>
            </button>
            <div
              className="calendar-heading text-center font-semibold text-lg p-2"
              style={{
                backgroundColor: "#EEF3FF",
                width: "90%",
                justifySelf: "anchor-center",
                color: "#43567D",
              }}
            >
              {t("Your Schedule for")}{" "}
              {new Date(currentYear, currentMonth).toLocaleString(
                i18n.language === "hi" ? "hi-IN" : "en-US",
                { month: "long" }
              )}{" "}
              {currentYear}
            </div>{" "}
            <button
              className="month-nav-button btn btn-sm btn-light"
              style={{ width: "4%", color: "#9F1D21" }}
              onClick={handleNextMonth}
              type="button"
            >
              <span className="fs-4">&gt;</span>
            </button>
          </div>

          {/* Calendar Table */}
          <table
            className="calendar-table table-fixed border-collapse bg-white shadow rounded-md mx-auto"
            style={{ width: "95%" }}
          >
            <thead>
              <tr>
                {daysOfWeek.map((day) => (
                  <th
                    key={day}
                    className="border p-2 bg-gray-100 text-sm font-medium"
                    style={{ color: "#969696" }}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((week, i) => (
                <tr key={i}>
                  {week.map((cell, j) => {
                    const isCurrent = cell.currentMonth;
                    const dateKey = cell.day;
                    const dataEntries = isCurrent ? calendarData[dateKey] : [];

                    return (
                      <td
                        key={j}
                        className="border p-1 text-sm align-top"
                        style={{
                          height: "120px",
                          width: "14.28%",
                          verticalAlign: "top",
                          color: isCurrent ? "#43567D" : "#B0B0B0",
                        }}
                      >
                        <div style={{ height: "100%" }}>
                          <div
                            className="day-number font-semibold mb-1"
                            style={{ textAlign: "left" }}
                          >
                            {cell.day}
                          </div>

                          {dataEntries?.map((entry, index) => (
                            <div
                              key={entry.id ?? index}
                              className={`schedule-entry small rounded px-2 py-1 mb-1 ${getStatusColor(
                                entry.status
                              )}`}
                              style={{
                                width: "fit-content",
                                fontSize: "0.75rem",
                                textAlign: "left",
                                marginTop: index === 0 ? "30%" : 0,
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCellClick(entry, cell);
                              }}
                            >
                              {convertTo12HourFormat(entry.time)} -{" "}
                              {t(entry.status)}
                            </div>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {isModalOpen && (
        <div
          className="schedule-modal-overlay"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="schedule-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <h2 className="schedule-modal-header">Update Time and Location</h2>

            {/* Content */}
            <div className="schedule-modal-content">
              {/* Time Picker Section */}
              <div className="schedule-time-section">
                <div className="schedule-time-picker">
                  {/* Hour */}
                  <div className="schedule-time-column">
                    <button
                      className="schedule-time-button"
                      onClick={incrementHour}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 15L12 8L19 15"
                          stroke="#354054"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className="schedule-time-value">{hour}</div>
                    <button
                      className="schedule-time-button"
                      onClick={decrementHour}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 9L12 16L5 9"
                          stroke="#354054"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Colon separator */}
                  <div className="schedule-time-separator">:</div>

                  {/* Minute */}
                  <div className="schedule-time-column">
                    <button
                      className="schedule-time-button"
                      onClick={incrementMinute}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 15L12 8L19 15"
                          stroke="#354054"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className="schedule-time-value">
                      {minute.toString().padStart(2, "0")}
                    </div>
                    <button
                      className="schedule-time-button"
                      onClick={decrementMinute}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 9L12 16L5 9"
                          stroke="#354054"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* AM/PM Toggle */}
                  <div className="schedule-ampm-toggle">
                    <button
                      className={`schedule-ampm-button ${
                        period === "AM" ? "active" : "inactive"
                      }`}
                      onClick={() => setPeriod("AM")}
                    >
                      AM
                    </button>
                    <button
                      className={`schedule-ampm-button ${
                        period === "PM" ? "active" : "inactive"
                      }`}
                      onClick={() => setPeriod("PM")}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>

              {/* Location Section */}
              <div className="schedule-location-section">
                <label className="schedule-location-label">
                  Enter Location
                </label>
                <input
                  type="text"
                  className="schedule-location-input"
                  placeholder="Type location..."
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="schedule-modal-footer">
              <button className="schedule-save-button" onClick={handleSave}>
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default ScheduleCalendar;
